// src/components/MoodSelector.js
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase-config.js";
import { resolveTipRecipient, sendTip, getTipTotals, hasTippedToday } from "../services/tipService.js";
import { subscribePaidTips } from "../services/tipPaymentService.js";
import TipPayModal from "./TipPayModal.js";
import { useToast } from "../context/ToastContext";



const moods = [
  { label: "Party", emoji: "🎉", color: "linear-gradient(135deg,#ec4899,#f43f5e)" },
  { label: "Chill", emoji: "😌", color: "linear-gradient(135deg,#f59e0b,#f97316)" },
  { label: "Energetic", emoji: "🔥", color: "linear-gradient(135deg,#8b5cf6,#6366f1)" },
  { label: "Romantic", emoji: "❤️", color: "linear-gradient(135deg,#ef4444,#ec4899)" },
];

// Celebratory "money + stars raining down" particles for a Tip the DJ tap.
const TIP_BURST_EMOJIS = ["💵", "💰", "⭐", "✨", "🌟", "🤑", "💸", "🌠"];
const makeTipBurst = () =>
  Array.from({ length: 28 }, (_, id) => ({
    id,
    emoji: TIP_BURST_EMOJIS[Math.floor(Math.random() * TIP_BURST_EMOJIS.length)],
    left: Math.random() * 100,             // vw
    delay: Math.random() * 0.45,           // s
    duration: 2.2 + Math.random() * 1.8,   // s (half speed)
    drift: (Math.random() * 2 - 1) * 60,   // px horizontal drift
    size: 18 + Math.random() * 22,         // px
    rotate: (Math.random() * 2 - 1) * 420, // deg
  }));

// `inline`  — render in normal document flow (no fixed bottom-pinned overlay),
//             e.g. embedded inside the request-confirmation modal.
// `showTip` — show the "Tip the DJ" pill (hidden in the inline modal variant).
function MoodSelector({ clientObj, moodButtonDailyLimit = 1, inline = false, showTip = true }) {
  const [todaySubmissionCount, setTodaySubmissionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastRequestedSong, setLastRequestedSong] = useState(null);

  const { addToast } = useToast();

  // Tip the DJ — symbolic points by default, real USD 1.00 on channels that
  // have paid tipping switched on (users/{venueUid}.tipsPaidEnabled).
  const venueUid = clientObj?.clientId || clientObj?.id || null;
  const [tipTotals, setTipTotals] = useState({ allTime: 0, tonight: 0 });
  const [tipping, setTipping] = useState(false);
  const [alreadyTipped, setAlreadyTipped] = useState(false);
  const [paidTips, setPaidTips] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [tipBurst, setTipBurst] = useState(null);
  const burstTimer = useRef(null);
  // Clear any pending burst timer on unmount.
  useEffect(() => () => { if (burstTimer.current) clearTimeout(burstTimer.current); }, []);

  // console.log('MoodSelector received moodButtonDailyLimit:', moodButtonDailyLimit, 'type:', typeof moodButtonDailyLimit);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Check how many mood submissions user has made today
  useEffect(() => {
    const checkTodaysMoodCount = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }

      try {
        const todayDate = getTodayDate();
        const moodQuery = query(
          collection(db, "crowdmood"),
          where("userId", "==", auth.currentUser.uid),
          where("date", "==", todayDate)
        );

        const querySnapshot = await getDocs(moodQuery);
        setTodaySubmissionCount(querySnapshot.size);
      } catch (error) {
        console.error("Error checking today's mood count:", error);
      }

      setLoading(false);
    };

    checkTodaysMoodCount();
  }, []);

  // Resolve who the tips credit (live guest DJ or channel owner) and load
  // their current session + all-time tip counters for the button.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!venueUid) return;
      const recipient = await resolveTipRecipient(venueUid);
      if (cancelled) return;
      const totals = await getTipTotals(recipient);
      if (!cancelled) setTipTotals(totals);
      // Have they already tipped this DJ this session?
      if (auth.currentUser) {
        const tipped = await hasTippedToday(recipient, auth.currentUser.uid);
        if (!cancelled) setAlreadyTipped(tipped);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [venueUid]);

  // Live-watch the venue's paid-tipping switch: toggling it in the dashboard
  // must change the button under guests who are already on the page.
  useEffect(() => {
    if (!venueUid) return undefined;
    return subscribePaidTips(venueUid, setPaidTips);
  }, [venueUid]);

  /** Celebrate + count a tip. Shared by the free and the paid paths. */
  const celebrateTip = () => {
    setTipTotals((t) => ({ allTime: t.allTime + 1, tonight: t.tonight + 1 }));
    setTipBurst(makeTipBurst());
    if (burstTimer.current) clearTimeout(burstTimer.current);
    burstTimer.current = setTimeout(() => setTipBurst(null), 5200);
  };

  const handleTip = async () => {
    if (!auth.currentUser || tipping || !venueUid) return;

    // Paid channels: hand off to PayPal. No once-per-night gate — a guest who
    // wants to tip the DJ twice is paying twice, and turning that away would
    // just be refusing money. Nothing is counted until the capture completes.
    if (paidTips) { setPayOpen(true); return; }

    // One tip per DJ per session (symbolic tips only).
    if (alreadyTipped) {
      addToast("You've already tipped this DJ this session — thanks! 🙌");
      return;
    }
    setTipping(true);
    setAlreadyTipped(true);
    // Optimistic bump so the counter reacts instantly, plus the 🎉 burst.
    celebrateTip();
    try {
      await sendTip(venueUid, auth.currentUser.uid);
      addToast("Thanks for tipping the DJ! 🙌");
    } catch (error) {
      console.error("Error sending tip:", error);
      // Roll back the optimistic changes so they can try again.
      setAlreadyTipped(false);
      setTipTotals((t) => ({
        allTime: Math.max(0, t.allTime - 1),
        tonight: Math.max(0, t.tonight - 1),
      }));
    }
    // Short cooldown to avoid accidental double-taps.
    setTimeout(() => setTipping(false), 600);
  };

  // Fetch user's last requested song
  useEffect(() => {
    const fetchLastRequest = async () => {
      if (!auth.currentUser) return;

      try {
        // Get user's most recent request
        const requestsQuery = query(
          collection(db, "users", auth.currentUser.uid, "requests"),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const requestSnapshot = await getDocs(requestsQuery);
        if (!requestSnapshot.empty) {
          const requestData = requestSnapshot.docs[0].data();
          const djchatId = requestData.djchatId;

          if (djchatId && clientObj?.id) {
            // Fetch the full song details from DJ's djchats
            const djChatRef = doc(db, "users", clientObj.id, "djchats", djchatId);
            const djChatSnap = await getDoc(djChatRef);

            if (djChatSnap.exists()) {
              const djChatData = djChatSnap.data();
              if (djChatData.request) {
                setLastRequestedSong({
                  track: djChatData.request.track,
                  artist: djChatData.request.artist,
                  coverart: djChatData.request.coverart
                });
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching last request:", error);
      }
    };

    fetchLastRequest();
  }, [clientObj]);
  const sendMood = async (mood) => {
    if (!auth.currentUser || todaySubmissionCount >= moodButtonDailyLimit || submitting) return;

    setSubmitting(true);
    try {
      const moodData = {
        userId: auth.currentUser.uid,
        clientId: clientObj?.id || null,
        mood,
        timestamp: serverTimestamp(),
        date: getTodayDate(),
      };

      // Add last requested song if available
      if (lastRequestedSong) {
        moodData.lastRequestedSong = lastRequestedSong;
      }

      await addDoc(collection(db, "crowdmood"), moodData);
      const used = todaySubmissionCount + 1;
      const remaining = Math.max(0, moodButtonDailyLimit - used);
      setTodaySubmissionCount(used);
      addToast(`Mood sent! ${used} of ${moodButtonDailyLimit} used, ${remaining} left today`);
    } catch (error) {
      console.error("Error saving mood:", error);
      alert("Failed to save your mood. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Root layout: inline (normal flow, e.g. embedded in the request modal) vs the
  // default fixed bottom-pinned overlay used on the Now Playing page.
  const rootClass = inline
    ? "flex flex-col items-center w-full"
    : "flex flex-col items-center fixed bottom-28 left-0 right-0 mood-selector-container";

  if (loading) {
    return (
      <div className={rootClass}>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const hasReachedLimit = todaySubmissionCount >= moodButtonDailyLimit;

  const counterBlock = (value, label) => (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#fbbf24", lineHeight: 1 }}>
        {Number(value || 0).toLocaleString()}
      </div>
      <div style={{ fontSize: 8, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9ca3af", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );

  return (
    <div className={rootClass}>
      {tipBurst && createPortal(
        <div
          aria-hidden="true"
          style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 99998, overflow: "hidden" }}
        >
          {tipBurst.map((p) => (
            <span
              key={p.id}
              style={{
                position: "absolute",
                top: "-8vh",
                left: `${p.left}vw`,
                fontSize: p.size,
                willChange: "transform, opacity",
                animation: `tipRain ${p.duration}s cubic-bezier(0.35,0,0.7,1) ${p.delay}s forwards`,
                "--tip-drift": `${p.drift}px`,
                "--tip-rot": `${p.rotate}deg`,
              }}
            >
              {p.emoji}
            </span>
          ))}
          <style>{`
            @keyframes tipRain {
              0%   { transform: translate(0, -10vh) rotate(0deg); opacity: 0; }
              12%  { opacity: 1; }
              88%  { opacity: 1; }
              100% { transform: translate(var(--tip-drift), 112vh) rotate(var(--tip-rot)); opacity: 0; }
            }
          `}</style>
        </div>,
        document.body
      )}
      <div style={{ width: "100%", maxWidth: "100%", background: "rgba(20,20,24,0.94)", borderRadius: 14, padding: "8px 10px", boxShadow: "0 8px 24px rgba(0,0,0,0.4)", display: "flex", alignItems: "stretch", gap: 8 }}>
        {/* Vertical "How are you feeling?" label — narrow left column,
            rotated 90° counter-clockwise (reads bottom-to-top). */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 8, fontWeight: 300, color: hasReachedLimit ? "#9ca3af" : "#f4f4f5", whiteSpace: "nowrap", letterSpacing: "0.02em", lineHeight: 1 }}>
            {hasReachedLimit ? `All ${moodButtonDailyLimit} moods used` : "How are you feeling?"}
          </span>
        </div>

        {/* Right column: tip row on top, mood buttons below */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
          {/* Tip the DJ — split pill (yellow border): amber "Tip the DJ" (exactly
              half) | black with yellow counters divided by a grey line. Hidden in
              the inline request-modal variant (showTip={false}). */}
          {showTip && (
          <button
            onClick={handleTip}
            disabled={tipping || !auth.currentUser}
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginLeft: 0,
              display: "flex", alignItems: "stretch",
              border: "1px solid #f59e0b", borderRadius: 999, overflow: "hidden", padding: 0,
              cursor: tipping ? "default" : "pointer",
              boxShadow: "0 4px 12px rgba(245,158,11,0.35)",
              transform: tipping ? "scale(0.98)" : "none",
              transition: "transform .12s ease",
            }}
          >
            {/* Left half — amber, exactly 50% */}
            <span style={{
              flex: "0 0 50%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              background: "linear-gradient(135deg,#f59e0b,#f0a020)", color: "#1a1305",
              fontWeight: 700, fontSize: 12, whiteSpace: "nowrap",
              padding: "6px 10px",
            }}>
              {/* On paid channels the price is on the button. A guest must never
                  reach a PayPal sheet from a control that looked free. */}
              <span style={{ fontSize: 14 }}>{paidTips ? "💵" : "👆"}</span>
              {paidTips ? "Tip the DJ · $1" : "Tip the DJ"}
            </span>
            {/* Right half — black with yellow counters + grey vertical divider */}
            <span style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "space-around",
              background: "#0d0d0f", padding: "6px 10px",
            }}>
              {counterBlock(tipTotals.tonight, "Tonight")}
              <span style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.25)", margin: "3px 0" }} />
              {counterBlock(tipTotals.allTime, "All time")}
            </span>
          </button>
          )}

          {payOpen && createPortal(
            <TipPayModal
              venueUid={venueUid}
              fromUid={auth.currentUser?.uid || null}
              onPaid={() => { celebrateTip(); addToast("Thanks for tipping the DJ! 🙌"); }}
              onClose={() => setPayOpen(false)}
            />,
            document.body
          )}

          {/* Moods — pill container; tiles have no square borders, just a grey
              vertical divider between each. */}
          <div style={{ width: "100%", boxSizing: "border-box", display: "flex", justifyContent: "center", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 999, padding: "6px 4px" }}>
            {moods.map((m, idx) => (
              <button
                key={m.label}
                onClick={() => sendMood(m.label)}
                disabled={hasReachedLimit || submitting}
                style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                  background: "transparent", border: "none", borderRadius: 0,
                  borderLeft: idx > 0 ? "1px solid rgba(255,255,255,0.14)" : "none",
                  padding: "2px 3px",
                  cursor: hasReachedLimit || submitting ? "not-allowed" : "pointer",
                  opacity: hasReachedLimit || submitting ? 0.5 : 1,
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", background: m.color, fontSize: 12 }}>
                  {m.emoji}
                </span>
                <span style={{ fontSize: 9, fontWeight: 300, color: "#e5e7eb" }}>{m.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MoodSelector;
